import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { TooltipComponent, GridComponent, LegendComponent } from 'echarts/components';
import { BarChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { useAppContext } from '../../../../providers/AppProvider';
import { Card } from 'react-bootstrap';
import React from 'react';

echarts.use([TooltipComponent, GridComponent, LegendComponent, BarChart, CanvasRenderer]);

interface FindingDataItem {
  Event_Index: number;
  PIRS: number;
  Risk: string;
  Verdict: string;
  FAISS_Similarity: number;
  Classifier_Probability: number;
  Toxicity_Score: number;
  Obfuscation_Flag: number;
  Prompt_Preview: string;
}

interface PromptInjectionFindingsChartProps {
  data: FindingDataItem[];
  title: string;
  description: string;
}

const PromptInjectionFindingsChart: React.FC<PromptInjectionFindingsChartProps> = ({ data, title, description }) => {
  const { getThemeColor } = useAppContext();

  // Sort data by event index
  const sortedData = React.useMemo(() => {
    return [...data].sort((a, b) => a.Event_Index - b.Event_Index);
  }, [data]);

  // Ensure we have valid colors with fallbacks
  const primaryColor = getThemeColor('primary') || '#0d6efd';
  const infoColor = getThemeColor('info') || '#0dcaf0';
  const warningColor = getThemeColor('warning') || '#ffc107';
  const dangerColor = getThemeColor('danger') || '#dc3545';
  const bodyHighlightBg = getThemeColor('body-highlight-bg') || '#ffffff';
  const borderColor = getThemeColor('border-color') || '#dee2e6';
  const secondaryColor = getThemeColor('secondary-color') || '#6c757d';
  const lightTextEmphasis = getThemeColor('light-text-emphasis') || '#495057';

  const eventIndices = sortedData.map(item => `Event ${item.Event_Index}`);
  const pirsValues = sortedData.map(item => item.PIRS);
  const faissValues = sortedData.map(item => item.FAISS_Similarity);
  const classifierValues = sortedData.map(item => item.Classifier_Probability);
  const toxicityValues = sortedData.map(item => item.Toxicity_Score);

  const options = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      },
      backgroundColor: bodyHighlightBg,
      borderColor: borderColor,
      textStyle: { color: lightTextEmphasis },
      borderWidth: 1,
      formatter: (params: any) => {
        const eventIndex = sortedData[params[0].dataIndex].Event_Index;
        const finding = sortedData[params[0].dataIndex];
        let tooltip = `<strong>Event ${eventIndex}</strong><br/>`;
        params.forEach((param: any) => {
          tooltip += `${param.seriesName}: ${param.value.toFixed(2)}%<br/>`;
        });
        tooltip += `<strong>Risk:</strong> ${finding.Risk}<br/>`;
        tooltip += `<strong>Verdict:</strong> ${finding.Verdict}`;
        return tooltip;
      }
    },
    legend: {
      data: ['PIRS', 'FAISS Similarity', 'Classifier Probability', 'Toxicity'],
      top: '5%',
      textStyle: { color: lightTextEmphasis }
    },
    xAxis: {
      type: 'category',
      name: 'Event',
      data: eventIndices,
      axisLine: { lineStyle: { color: borderColor } },
      axisLabel: {
        color: secondaryColor,
        rotate: 45
      },
      nameTextStyle: { color: secondaryColor }
    },
    yAxis: {
      type: 'value',
      name: 'Score (%)',
      min: 0,
      max: 100,
      axisLine: { lineStyle: { color: borderColor } },
      axisLabel: { color: secondaryColor },
      nameTextStyle: { color: secondaryColor },
      splitLine: {
        show: true,
        lineStyle: {
          color: borderColor,
          type: 'dashed'
        }
      }
    },
    series: [
      {
        name: 'PIRS',
        type: 'bar',
        data: pirsValues,
        itemStyle: {
          color: primaryColor,
          borderRadius: [5, 5, 0, 0]
        }
      },
      {
        name: 'FAISS Similarity',
        type: 'bar',
        data: faissValues,
        itemStyle: {
          color: infoColor,
          borderRadius: [5, 5, 0, 0]
        }
      },
      {
        name: 'Classifier Probability',
        type: 'bar',
        data: classifierValues,
        itemStyle: {
          color: warningColor,
          borderRadius: [5, 5, 0, 0]
        }
      },
      {
        name: 'Toxicity',
        type: 'bar',
        data: toxicityValues,
        itemStyle: {
          color: dangerColor,
          borderRadius: [5, 5, 0, 0]
        }
      }
    ],
    grid: { left: '10%', right: '10%', bottom: '20%', top: '20%' }
  };

  return (
    <Card className="h-100">
      <Card.Body>
        <div className="d-flex justify-content-between">
          <div>
            <h4 className="mb-1">{title}</h4>
            <h6 className="text-body-tertiary">{description}</h6>
          </div>
        </div>
        <div className="d-flex justify-content-center px-4 py-6">
          <ReactEChartsCore echarts={echarts} option={options} style={{ height: '400px', width: '100%' }} />
        </div>
      </Card.Body>
    </Card>
  );
};

export default PromptInjectionFindingsChart;
